type AppTarget = "leadplace" | "legal" | "storefront";

interface BuildVersion {
  buildId: string;
  appTarget: AppTarget;
}

type RecoveryReason = "update" | "load-error";

const versionCheckIntervalMs = 60_000;
let installed = false;
let versionCheck: Promise<boolean> | null = null;
let pendingRoute: string | null = null;
let shownReason: RecoveryReason | null = null;

const isBuildVersion = (value: unknown): value is BuildVersion => {
  if (!value || typeof value !== "object") return false;
  const version = value as Partial<BuildVersion>;
  return (
    typeof version.buildId === "string" &&
    version.buildId.length > 0 &&
    (version.appTarget === "leadplace" ||
      version.appTarget === "legal" ||
      version.appTarget === "storefront")
  );
};

const currentVersion = (): BuildVersion | null => {
  const meta = document.querySelector<HTMLMetaElement>(
    'meta[name="app-build-id"]',
  );
  const value = { buildId: meta?.content, appTarget: meta?.dataset.appTarget };
  return isBuildVersion(value) ? value : null;
};

const showRecovery = (reason: RecoveryReason): void => {
  if (shownReason === "update" && reason === "load-error") return;
  const notice = document.getElementById("app-recovery");
  const title = notice?.querySelector("strong");
  const description = notice?.querySelector("p");
  const button = notice?.querySelector("button");
  if (!notice || !title || !description || !button) return;

  shownReason = reason;
  title.textContent =
    reason === "update"
      ? "Доступна новая версия"
      : "Не удалось загрузить страницу";
  description.textContent =
    reason === "update"
      ? "Сохраните изменения и обновите страницу. Несохранённый ввод будет потерян."
      : "Сохраните изменения и попробуйте обновить страницу. Несохранённый ввод будет потерян.";
  button.onclick = () => {
    if (pendingRoute) {
      const destination = new URL(pendingRoute, window.location.origin);
      if (destination.origin === window.location.origin) {
        window.location.assign(destination.href);
        return;
      }
    }
    window.location.reload();
  };
  notice.hidden = false;
};

export const checkForAppUpdate = (): Promise<boolean> => {
  const activeVersion = currentVersion();
  if (!import.meta.env.PROD || !activeVersion) return Promise.resolve(false);
  if (versionCheck) return versionCheck;

  versionCheck = (async () => {
    try {
      const response = await fetch("/version.json", { cache: "no-store" });
      if (!response.ok) return false;
      const candidate: unknown = await response.json();
      if (!isBuildVersion(candidate)) return false;
      if (
        candidate.appTarget !== activeVersion.appTarget ||
        candidate.buildId === activeVersion.buildId
      ) {
        return false;
      }
      showRecovery("update");
      return true;
    } catch {
      return false;
    }
  })().finally(() => {
    versionCheck = null;
  });
  return versionCheck;
};

const isModuleLoadError = (error: unknown): boolean => {
  if (!(error instanceof Error)) return false;
  return /failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed|failed to load module script|unable to preload css|loading chunk .+ failed|failed to reload .+|outdated optimize dep/i.test(
    error.message,
  );
};

export const reportModuleLoadFailure = (
  error: unknown,
  route?: string,
): void => {
  console.error("Failed to load application module:", error);
  if (!isModuleLoadError(error)) return;
  if (route) pendingRoute = route;
  void checkForAppUpdate().then((updated) => {
    if (!updated) showRecovery("load-error");
  });
};

export const reportEntryLoadFailure = (error: unknown): void => {
  console.error("Failed to start application:", error);
  void checkForAppUpdate().then((updated) => {
    if (!updated) showRecovery("load-error");
  });
};

export const installAppRecovery = (): void => {
  if (installed) return;
  installed = true;
  window.addEventListener("vite:preloadError", (event: Event) => {
    reportModuleLoadFailure((event as Event & { payload?: unknown }).payload);
  });

  if (!import.meta.env.PROD) return;
  void checkForAppUpdate();
  window.setInterval(() => {
    if (document.visibilityState === "visible") void checkForAppUpdate();
  }, versionCheckIntervalMs);
  window.addEventListener("focus", () => {
    if (document.visibilityState === "visible") void checkForAppUpdate();
  });
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") void checkForAppUpdate();
  });
  window.addEventListener("online", () => {
    if (document.visibilityState === "visible") void checkForAppUpdate();
  });
};
